import React, { useState, useRef, useEffect } from 'react';
import {
    Bold, Italic, List, ListOrdered, Sparkles,
    MoreHorizontal, Wand2, Check, X, Loader2,
    AlignLeft, AlignCenter, AlignRight,
    Type, Edit3, RefreshCw, Maximize2, Minimize2,
    ArrowLeft, Palette, Undo, LayoutTemplate, PlusCircle
} from 'lucide-react';
import { refineWebsiteText } from '../services/geminiService';

export interface StandardSectionData {
    id: string;
    name: string;
    content: string;
}

interface AiRichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    minHeight?: string;
    className?: string;
    aiMode?: 'suggest' | 'replace';
    onBlur?: () => void;
    hideAiFeatures?: boolean;
    readOnly?: boolean;
    standardSections?: StandardSectionData[];
    defaultAiPrompt?: string;
}

const TONE_OPTIONS = [
    { label: 'Formeel', value: 'Formal' },
    { label: 'Vriendelijk', value: 'Friendly' },
    { label: 'Persoonlijk', value: 'Personal' },
    { label: 'Wervend', value: 'Engaging' }
];

const parseSimpleMarkdown = (text: string) => {
    if (!text) return "";
    // If it already contains typical HTML tags, return as is (Gemini followed prompt)
    if (text.includes('<ul>') || text.includes('<li>') || text.includes('<p>') || text.includes('<br/>')) {
        return text;
    }

    let inList = false;
    const lines = text.split('\n');
    const htmlLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        let line = lines[i].trim();
        
        // Match Markdown bullet points
        if (line.startsWith('* ') || line.startsWith('- ') || line.startsWith('• ')) {
            if (!inList) {
                htmlLines.push('<ul>');
                inList = true;
            }
            // Parse bold text within the list item
            line = line.substring(2).trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>');
            htmlLines.push(`<li>${line}</li>`);
        } else {
            if (inList) {
                htmlLines.push('</ul>');
                inList = false;
            }
            
            if (line) {
                // Parse bold and italics within paragraphs
                line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
                htmlLines.push(`${line} `);
            } else {
                htmlLines.push('<br/><br/>');
            }
        }
    }
    
    if (inList) {
        htmlLines.push('</ul>');
    }
    
    // Clean up excessive line breaks after lists
    return htmlLines.join('').replace(/<\/ul><br\/>/g, '</ul>');
};

const AiRichTextEditor: React.FC<AiRichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Start typing...',
    label,
    minHeight = '300px',
    className = '',
    aiMode = 'suggest',
    onBlur,
    hideAiFeatures = false,
    readOnly = false,
    standardSections = [],
    defaultAiPrompt
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isAiLoading, setIsAiLoading] = useState(false);

    // Floating Menu State
    const [selectionRange, setSelectionRange] = useState<Range | null>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number, left: number } | null>(null);
    const [showToneMenu, setShowToneMenu] = useState(false);

    // Sync content
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value && !isFocused) {
            editorRef.current.innerHTML = value;
        }
    }, [value, isFocused]);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        if (text) {
            const html = parseSimpleMarkdown(text);
            document.execCommand('insertHTML', false, html);
        }
    };

    // Selection Handling for Floating Menu
    useEffect(() => {
        const handleSelectionChange = () => {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
                setMenuPosition(null);
                setSelectionRange(null);
                setShowToneMenu(false);
                return;
            }

            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();

            // Check if selection is inside our editor
            if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
                setSelectionRange(range);
                setMenuPosition({
                    top: rect.bottom + 10, // 10px below the selection instead of 50px above
                    left: rect.left + (rect.width / 2) // Centered
                });
            } else {
                setMenuPosition(null);
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    const execCommand = (command: string, value: string | undefined = undefined) => {
        document.execCommand(command, false, value);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
        editorRef.current?.focus();
    };

    const [undoStack, setUndoStack] = useState<string[]>([]);

    const handleUndo = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        if (undoStack.length > 0) {
            const previousHtml = undoStack[undoStack.length - 1];
            setUndoStack(prev => prev.slice(0, -1));
            if (editorRef.current) {
                editorRef.current.innerHTML = previousHtml;
                onChange(previousHtml);
            }
        }
    };

    const handleAiAction = async (action: string, param?: string) => {
        if (!selectionRange) return;

        const selectedText = selectionRange.toString();
        if (!selectedText.trim()) return;

        setIsAiLoading(true);
        try {
            const currentHtml = editorRef.current?.innerHTML || "";
            
            let instruction = "";
            switch (action) {
                case 'edit': instruction = "Corrigeer de spelling, grammatica en zinsbouw van deze tekst. Behoud de inhoud."; break;
                case 'rewrite': instruction = "Herschrijf deze tekst om het vloeiender en professioneler te maken."; break;
                case 'shorter': instruction = "Maak deze tekst korter en krachtiger zonder essentiële informatie te verliezen."; break;
                case 'longer': instruction = "Breid deze tekst uit met relevante details en heldere uitleg."; break;
                case 'tone': instruction = `Herschrijf deze tekst in een ${param} toon.`; break;
            }

            const refined = await refineWebsiteText(selectedText, instruction);

            // Apply refined text
            if (refined) {
                const htmlRefined = parseSimpleMarkdown(refined);
                setUndoStack(prev => [...prev, currentHtml]);
                
                selectionRange.deleteContents();
                
                const wrapper = document.createElement("span");
                if (aiMode === 'replace') {
                    wrapper.innerHTML = htmlRefined;
                } else {
                    wrapper.innerHTML = `${selectedText}<br/><br/><strong>✨ AI Suggestie:</strong><br/>${htmlRefined}<br/>`;
                }
                
                selectionRange.insertNode(wrapper);
                if (editorRef.current) onChange(editorRef.current.innerHTML);
            }
        } catch (e) {
            console.error("AI Error:", e);
        } finally {
            setIsAiLoading(false);
            setMenuPosition(null); // Hide menu after action
        }
    };

    const [showImproveModal, setShowImproveModal] = useState(false);
    const [showSectionsDropdown, setShowSectionsDropdown] = useState(false);
    const [customPrompt, setCustomPrompt] = useState("");

    const insertStandardSection = (sectionHtml: string) => {
        if (!editorRef.current) return;
        const currentHtml = editorRef.current.innerHTML;
        setUndoStack(prev => [...prev, currentHtml]);
        
        editorRef.current.focus();
        // Use document.execCommand to insert at cursor position
        document.execCommand('insertHTML', false, `<br><br>${sectionHtml}`);
        onChange(editorRef.current.innerHTML);
        setShowSectionsDropdown(false);
    };

    const handleFullImprovement = async () => {
        if (!editorRef.current) return;

        // Close modal if open
        setShowImproveModal(false);
        setCustomPrompt(""); // Reset prompt

        setIsAiLoading(true);
        try {
            // Generate New vs Improve Existing within Custom Prompt context
            // If custom prompt exists, use it. Otherwise use default logic.
            const currentHtml = editorRef.current.innerHTML;
            const currentText = editorRef.current.innerText.trim();
            const isCreation = !currentText;

            let prompt = "";
            if (customPrompt) {
                prompt = customPrompt;
            } else if (defaultAiPrompt) {
                prompt = defaultAiPrompt;
            } else {
                prompt = isCreation
                    ? `Schrijf een wervende en professionele ${label || 'tekst'} voor een vacature.`
                    : "Verbeter de spelling, grammatica, structuur en toon van deze tekst. Maak het wervend en professioneel.";
            }

            const refined = await refineWebsiteText(editorRef.current.innerText, prompt);

            if (refined) {
                const htmlRefined = parseSimpleMarkdown(refined);
                setUndoStack(prev => [...prev, currentHtml]);
                
                let newHtml = "";
                if (isCreation || aiMode === 'replace') {
                    newHtml = htmlRefined;
                } else {
                    newHtml = currentHtml + `<br><br><strong>✨ Froukje's Suggestie:</strong><br>${htmlRefined}<br>`;
                }

                editorRef.current.innerHTML = newHtml; // Use innerHTML to preserve potential formatting
                onChange(newHtml);
            }
        } catch (e) {
            console.error("Full AI improvement error", e);
        } finally {
            setIsAiLoading(false);
        }
    }

    const hasContent = value && value.trim().length > 0;

    return (
        <div className={`bg-white border border-slate-200 rounded-lg flex flex-col ${className} focus-within:ring-2 focus-within:ring-purple-50`}>
            {label && <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-3 py-2 bg-slate-50 border-b border-slate-100 block rounded-t-lg">{label}</label>}

            {/* Toolbar */}
            <div className={`transition-all ${isFocused ? 'border-purple-400 ring-4 ring-purple-50 shadow-md rounded-t-lg' : ''}`}>

                {/* Toolbar — Rij 1: opmaaktools */}
                <div className="flex items-center flex-wrap gap-1 px-3 py-2 border-b border-slate-100 bg-slate-50/50">
                    <button onClick={() => execCommand('bold')} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition-colors" title="Bold"><Bold size={14} /></button>
                    <button onClick={() => execCommand('italic')} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition-colors" title="Italic"><Italic size={14} /></button>
                    <button onClick={() => execCommand('underline')} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition-colors" title="Underline"><Type size={14} className="underline" /></button>
                    <div className="w-px h-4 bg-slate-200 mx-0.5" />

                    <select onChange={(e) => execCommand('fontName', e.target.value)} className="bg-transparent text-xs text-slate-600 outline-none border-none p-1 hover:bg-slate-100 rounded cursor-pointer" title="Lettertype">
                        <option value="">Lettertype</option>
                        <option value="Inter, sans-serif">Inter</option>
                        <option value="ui-serif, Georgia, serif">Serif</option>
                        <option value="ui-monospace, Consolas, monospace">Mono</option>
                    </select>

                    <select onChange={(e) => execCommand('fontSize', e.target.value)} className="bg-transparent text-xs text-slate-600 outline-none border-none p-1 hover:bg-slate-100 rounded cursor-pointer" title="Lettergrootte">
                        <option value="">Grootte</option>
                        <option value="1">Klein</option>
                        <option value="3">Normaal</option>
                        <option value="5">Groot</option>
                        <option value="7">Extra Groot</option>
                    </select>

                    <div className="relative flex items-center p-1 hover:bg-slate-200 rounded transition-colors" title="Tekstkleur">
                        <Palette size={14} className="text-slate-500 mr-1" />
                        <input type="color" onChange={(e) => execCommand('foreColor', e.target.value)} className="w-4 h-4 p-0 border-0 bg-transparent cursor-pointer rounded-sm" />
                    </div>

                    <div className="w-px h-4 bg-slate-200 mx-0.5" />
                    <button onClick={() => execCommand('insertUnorderedList')} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition-colors" title="List"><List size={14} /></button>
                    <button onClick={() => execCommand('insertOrderedList')} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition-colors" title="Ordered List"><ListOrdered size={14} /></button>
                </div>

                {/* Toolbar — Rij 2: AI-knop (altijd zichtbaar, nooit overflow) */}
                {!hideAiFeatures && (
                    <div className="relative flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 bg-slate-50/80">
                        {undoStack.length > 0 && (
                            <button onClick={handleUndo} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-100 text-orange-700 text-[10px] font-black uppercase tracking-wider hover:bg-orange-200 transition-colors">
                                <Undo size={12} /> Herstel
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setShowImproveModal(!showImproveModal);
                                setShowSectionsDropdown(false);
                            }}
                            disabled={isAiLoading}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[10px] font-black uppercase tracking-wider hover:shadow-lg hover:shadow-purple-500/20 transition-all active:scale-95 disabled:opacity-70"
                        >
                            {isAiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            {hasContent ? 'Verbeter met AI' : 'Schrijf met AI'}
                        </button>

                        {standardSections && standardSections.length > 0 && (
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowSectionsDropdown(!showSectionsDropdown);
                                        setShowImproveModal(false);
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 transition-all active:scale-95"
                                >
                                    <LayoutTemplate size={12} className="text-amber-500" />
                                    Sectie Invoegen
                                </button>

                                {showSectionsDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 p-2 z-50 max-h-64 overflow-y-auto">
                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2 pb-2 mb-1 border-b border-slate-100">Kies Standaard Sectie</div>
                                        {standardSections.map(s => (
                                            <button 
                                                key={s.id}
                                                onClick={() => insertStandardSection(s.content)}
                                                className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-amber-50 hover:text-amber-700 rounded-lg flex items-center gap-2 transition-colors"
                                            >
                                                <PlusCircle size={12} className="opacity-50" /> {s.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* CUSTOM PROMPT POPUP — anchor left so it never goes off-screen */}
                        {showImproveModal && (
                            <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-purple-100 p-4 z-50">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-xs font-black text-purple-900 uppercase">Instructie voor Froukje</h4>
                                    <button onClick={() => setShowImproveModal(false)}><X size={14} className="text-slate-400 hover:text-slate-600" /></button>
                                </div>
                                <textarea
                                    className="w-full text-xs p-3 bg-slate-50 border border-slate-200 rounded-lg mb-3 focus:ring-2 focus:ring-purple-500 outline-none min-h-[80px]"
                                    placeholder={hasContent ? 'Bv. Maak het formeler, focus op teamwork...' : 'Bv. Schrijf een introductie over...'}
                                    value={customPrompt}
                                    onChange={(e) => setCustomPrompt(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    onClick={handleFullImprovement}
                                    className="w-full py-2 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Sparkles size={12} />
                                    {customPrompt ? 'Uitvoeren' : 'Verbeter Automatisch'}
                                </button>
                            </div>
                        )}
                    </div>
                )}


                {/* Editor Area */}
                <div
                    ref={editorRef}
                    contentEditable={!readOnly}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                        setIsFocused(false);
                        if (onBlur) onBlur();
                    }}
                    className="p-4 outline-none prose prose-sm max-w-none text-slate-700 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 min-h-[150px] rich-text-editor"
                    style={{ minHeight }}
                    data-placeholder={placeholder}
                />
            </div>

            {/* FLOATING AI MENU */}
            {!hideAiFeatures && menuPosition && (
                <div
                    className="fixed z-50 flex items-center gap-1 p-1 bg-white rounded-xl shadow-xl border border-purple-100 animate-in fade-in zoom-in-95 duration-200"
                    style={{
                        top: menuPosition.top,
                        left: menuPosition.left,
                        transform: 'translateX(-50%)'
                    }}
                    onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
                >
                    <div className="flex items-center gap-1 pr-2 border-r border-slate-100 mr-1">
                        <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                            {isAiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        </div>
                        <span className="text-xs font-bold text-purple-900 mr-2">AI</span>
                    </div>

                    {!showToneMenu ? (
                        <>
                            <button onClick={() => handleAiAction('edit')} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1">
                                <Edit3 size={12} /> Edit
                            </button>
                            <button onClick={() => handleAiAction('rewrite')} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1">
                                <RefreshCw size={12} /> Rewrite
                            </button>
                            <button onClick={() => handleAiAction('longer')} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1">
                                <Maximize2 size={12} /> Make longer
                            </button>
                            <button onClick={() => handleAiAction('shorter')} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1">
                                <Minimize2 size={12} /> Make shorter
                            </button>
                            <button onClick={() => setShowToneMenu(true)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1">
                                <Wand2 size={12} /> Change tone <MoreHorizontal size={10} />
                            </button>
                        </>
                    ) : (
                        <div className="flex items-center gap-1">
                            <button onClick={() => setShowToneMenu(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"><ArrowLeft size={12} /></button>
                            <button onClick={() => handleAiAction('tone', 'Formeel')} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg">Formeel</button>
                            <button onClick={() => handleAiAction('tone', 'Vriendelijk')} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg">Vriendelijk</button>
                            <button onClick={() => handleAiAction('tone', 'Persoonlijk')} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg">Persoonlijk</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiRichTextEditor;

